import classnames from "classnames";
import Button from "components/Button/Button";
import ButtonGroup from "components/ButtonGroup/ButtonGroup";
import { PagesUtils, StoreUtils } from "funkis-foundation";
import Text from "funkis-template/components/core/Text";
import gsap from "gsap";
import _ from "lodash";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { makeGetCurrentPage } from "../../../selectors/playerSelectors";
import { updateLocation } from "../../actions/Actions";
import Style from "./PageNavigation.module.css";

const { getSibling } = PagesUtils;

const Fade: FC<{ visible: boolean }> = ({ visible, children }) => {
	const elementRef = useRef<HTMLSpanElement>(null);

	const timeline = useRef<gsap.core.Timeline>();
	const [initiallyVisible] = useState(visible);

	useEffect(() => {
		let ctx = gsap.context(() => {
			timeline.current = gsap
				.timeline({
					paused: true,
				})
				.fromTo(
					elementRef.current,
					{
						autoAlpha: 0,
					},
					{ autoAlpha: 1, duration: 0.5, ease: "power4.inOut" }
				);

			timeline.current.progress(initiallyVisible ? 1 : 0);
		}, elementRef);

		return () => ctx.revert();
	}, [initiallyVisible]);

	useEffect(() => {
		visible ? timeline.current?.play() : timeline.current?.reverse();
	}, [visible]);

	return <span ref={elementRef}>{children}</span>;
};

type PageNavigationProps = {
	className?: string;
	show: boolean;
	nextTextId?: string;
	previousTextId?: string;
	menuTextId?: string;
	disableNextNavigation: boolean;
};

const selectCurrentPage = makeGetCurrentPage();
const { getPageByPath } = PagesUtils;

const PageNavigation: FC<PageNavigationProps> = (props) => {
	const {
		className,
		show,
		disableNextNavigation,
		nextTextId = "FAA5BD0C-8239-4FAF-8301-BCDC32ED8654",
		previousTextId = "E2F25B30-76CC-4AE0-8BC6-708A124BEEB8",
		menuTextId = "8931969D-F644-46D9-8B13-97000804C5A4",
	} = props;
	const pages = StoreUtils.getReducer("pages").items;
	const currentPage = useSelector((state) => selectCurrentPage(state, props));

	const previousPage = getSibling(currentPage.id, pages, -1);
	const nextPage = getSibling(currentPage.id, pages, 1);

	const currentChapter = pages.find((p) => p.id === currentPage.parentPageId);
	const showNextChapterButton =
		!nextPage &&
		_.get(currentChapter, "data.cmtPage.show_next_chapter_button") === "yes";

	const hasNextChapter = getPageByPath(
		[{ parent: 1 }, { sibling: 1 }, { child: 0 }],
		currentPage.id
	);

	return (
		<div className={classnames(Style.root, className)}>
			<Fade visible={show}>
				<ButtonGroup direction="horizontal">
					{/* PREVIOUS PAGE */}
					{previousPage !== undefined && (
						<Button
							variant="outline"
							onClick={() => {
								updateLocation({
									pageId: currentPage && currentPage.id,
									path: [{ sibling: -1 }],
								});
							}}
						>
							<Text textId={previousTextId} />
						</Button>
					)}

					{/* NEXT PAGE */}
					{nextPage !== undefined && (
						<Button
							disabled={disableNextNavigation}
							onClick={() => {
								updateLocation({
									pageId: currentPage && currentPage.id,
									path: [{ sibling: 1 }],
								});
							}}
						>
							<Text textId={nextTextId} />
						</Button>
					)}

					{/* MENU */}
					{nextPage === undefined && (
						<Button
							disabled={disableNextNavigation}
							variant={
								showNextChapterButton && hasNextChapter ? "outline" : "solid"
							}
							onClick={() => {
								/* @ts-expect-error updateLocation's auto generated types are wonky */
								updateLocation({ friendlyId: "menu" });
							}}
						>
							<Text textId={menuTextId} />
						</Button>
					)}

					{/* NEXT CHAPTER */}
					{showNextChapterButton && hasNextChapter && (
						<Button
							disabled={disableNextNavigation}
							onClick={() => {
								updateLocation({
									pageId: currentPage && currentPage.id,
									// next chapter page
									path: [{ parent: 1 }, { sibling: 1 }, { child: 0 }],
								});
							}}
						>
							<Text textId={nextTextId} />
						</Button>
					)}
				</ButtonGroup>
			</Fade>
		</div>
	);
};

export default PageNavigation;
